/**
 * The Bexio sync history (`/admin/bexio/sync-runs`).
 *
 * The operator question this answers, in order: when did data last arrive from
 * Bexio, is syncing healthy over the chosen window, and what did one particular
 * run do. Everything on the page serves one of those three; nothing decorates.
 *
 * THIS FILE IS THE ORCHESTRATOR. It owns the filter state, the shared window
 * instant, the four reads and the selected run; the pieces below own no data of
 * their own and fetch nothing:
 *   `BexioSyncRunsKpiRow`      — the four window KPIs
 *   `BexioSyncRunsMetaStrip`   — connection, last successful sync, timezone
 *   `BexioSyncRunsFilterBar`   — scope / status / period, fully controlled
 *   `BexioSyncRunsTable`       — head, rows, states and pager
 *   `BexioSyncRunDetailPanel`  — the selected run, as a column (lg and up)
 *   `BexioSyncRunDetailDrawer` — the same run, as an overlay (below lg)
 *   `BexioSyncRunsDailyChart`  — the fixed 14-day history, as stacked bars
 * The two detail surfaces share `BexioSyncRunDetailContent`; neither owns a
 * field list of its own.
 *
 * ONE SELECTION, HELD AS AN ID. `selectedId` is the single source, and the run
 * shown is looked up in the CURRENT rows. That is not a detail: holding the run
 * OBJECT would let the panel keep displaying a run that a filter change or a page
 * turn has removed from the table, and no effect can fix that as reliably as
 * never being able to express it. `drawerOpen` is not a second selection — it is
 * whether the small-screen overlay is up, and only a click sets it, so the
 * automatic selection below never opens a sheet in a reader's face.
 *
 * FOUR READS, FOUR FAILURE DOMAINS.
 *   `status`  → the "last successful sync" card, and only that card.
 *   `summary` → the three window KPIs, and only those.
 *   `list`    → the table, and only the table.
 *   `daily`   → the history chart, and only the chart.
 * They load independently and fail independently on purpose. A single
 * `Promise.all` would let a broken summary blank a table that is working
 * perfectly well — which is exactly when an operator needs the table most. The
 * children receive one loading/error pair per domain for the same reason: a
 * single shared flag would re-couple what these four effects keep apart.
 *
 * THE CHART READS A DIFFERENT WINDOW ON PURPOSE. The other three follow the
 * period filter; the chart is pinned to `DAILY_CHART_DAYS` and to the two data
 * scopes, so its heading means the same thing whatever the table is showing.
 * Widening it with the filter would make "die letzten 14 Tage" a caption that
 * silently describes 90 — and it is a CALENDAR window besides, bucketed in
 * Europe/Zurich by the backend, where the other three take instants.
 *
 * NO NUMBER IS COMPUTED HERE. The counts, the success rate and the average
 * duration come from SQL aggregates the backend proves against real PostgreSQL
 * (`backend/tests/test_bexio_sync_run_read_postgres.py`). Recomputing any of
 * them from the loaded page would produce a figure about 25 rows while the label
 * claims 30 days. The one arithmetic exception is a single run's own duration,
 * which is display maths over the row the reader is looking at.
 *
 * WHY THE STATUS FILTER DOES NOT REACH THE SUMMARY. The summary IS the breakdown
 * by status. Passing the table's status filter into it would answer "how healthy
 * is this window" with a set selected for its status — filter to errors and the
 * success rate becomes 0 %, which describes the filter, not the system. Scope and
 * period move both; status moves only the table. The endpoint does not accept a
 * status parameter either, so this split is the contract, not a preference.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  fetchBexioStatus,
  fetchBexioSyncRuns,
  fetchBexioSyncRunsDaily,
  fetchBexioSyncRunsSummary,
} from "../../../api/adminData/bexio";
import { useBexioList } from "../../../components/admin/bexio/workspace/useBexioList";
import BexioSyncRunDetailDrawer from "./BexioSyncRunDetailDrawer";
import BexioSyncRunDetailPanel from "./BexioSyncRunDetailPanel";
import BexioSyncRunsDailyChart, {
  DAILY_CHART_DAYS,
} from "./BexioSyncRunsDailyChart";
import BexioSyncRunsFilterBar from "./BexioSyncRunsFilterBar";
import BexioSyncRunsKpiRow from "./BexioSyncRunsKpiRow";
import BexioSyncRunsMetaStrip from "./BexioSyncRunsMetaStrip";
import BexioSyncRunsTable from "./BexioSyncRunsTable";
import { useIsCompactViewport } from "./useIsCompactViewport";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PERIOD_DAYS,
  DEFAULT_SCOPES,
  buildSyncRunWindow,
  periodLabel,
} from "./bexioSyncRunsView";

/** Filter value -> the scopes actually sent. "data" expands to the two real ones. */
function scopesFor(scopeFilter) {
  return scopeFilter === "data" ? DEFAULT_SCOPES : [scopeFilter];
}

export default function BexioSyncRunsPage() {
  const [scopeFilter, setScopeFilter] = useState("data");
  const [statusFilter, setStatusFilter] = useState("");
  const [periodDays, setPeriodDays] = useState(DEFAULT_PERIOD_DAYS);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isCompact = useIsCompactViewport();

  /**
   * ONE instant per window selection, shared by the summary and the list.
   *
   * Recomputed only when the period or the scope changes — not on every render,
   * which would hand the two requests timestamps milliseconds apart and let a run
   * started in that gap land in one answer and not the other. `Date.now()` is read
   * inside the memo so tests can drive it by changing the inputs.
   */
  const range = useMemo(
    () => buildSyncRunWindow(periodDays, Date.now()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodDays, scopeFilter]
  );

  const scopes = useMemo(() => scopesFor(scopeFilter), [scopeFilter]);

  /* ── A. Status: the canonical "last successful sync" ───────────────── */
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setStatusLoading(true);
    fetchBexioStatus()
      .then((res) => {
        if (!alive) return;
        setStatus(res);
        setStatusError("");
      })
      .catch((e) => {
        if (!alive) return;
        setStatus(null);
        setStatusError(e?.message || "Status konnte nicht geladen werden.");
      })
      .finally(() => {
        if (alive) setStatusLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  /* ── B. Summary: the window aggregates ─────────────────────────────── */
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setSummaryLoading(true);
    // No `status` here — see the module docstring.
    fetchBexioSyncRunsSummary({
      entity_scope: scopes,
      started_from: range.startedFrom,
      started_to: range.startedTo,
    })
      .then((res) => {
        if (!alive) return;
        setSummary(res);
        setSummaryError("");
      })
      .catch((e) => {
        if (!alive) return;
        setSummary(null);
        setSummaryError(e?.message || "Kennzahlen konnten nicht geladen werden.");
      })
      .finally(() => {
        if (alive) setSummaryLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [scopes, range.startedFrom, range.startedTo]);

  /* ── C. List: the table ────────────────────────────────────────────── */
  const listFilters = useMemo(
    () => ({
      entity_scope: scopes,
      status: statusFilter ? [statusFilter] : undefined,
      started_from: range.startedFrom,
      started_to: range.startedTo,
    }),
    [scopes, statusFilter, range.startedFrom, range.startedTo]
  );

  const { items, total, loading, error, reload } = useBexioList({
    fetcher: fetchBexioSyncRuns,
    filters: listFilters,
    limit,
    offset,
  });

  const rows = useMemo(() => items || [], [items]);

  /* ── D. Daily: the fixed-window history chart ──────────────────────── */
  const [daily, setDaily] = useState(null);
  const [dailyError, setDailyError] = useState("");
  const [dailyLoading, setDailyLoading] = useState(true);

  /**
   * Fetched ONCE, with no filter in its dependency list.
   *
   * Not an oversight — the empty array IS the decision. The chart's window and
   * scopes are constants, so there is nothing on this page whose change should
   * re-issue this request. `DEFAULT_SCOPES` is passed because the chart shows the
   * runs that actually moved data; it is read as a module constant, never as the
   * current filter, which is why turning the filter to `init` leaves the bars
   * exactly where they were.
   */
  useEffect(() => {
    let alive = true;
    setDailyLoading(true);
    fetchBexioSyncRunsDaily({
      days: DAILY_CHART_DAYS,
      entity_scope: DEFAULT_SCOPES,
    })
      .then((res) => {
        if (!alive) return;
        setDaily(res);
        setDailyError("");
      })
      .catch((e) => {
        if (!alive) return;
        setDaily(null);
        setDailyError(e?.message || "Sync-Verlauf konnte nicht geladen werden.");
      })
      .finally(() => {
        if (alive) setDailyLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  /**
   * The run on show, looked up in the rows that are actually listed.
   *
   * A page turn or a filter change cannot leave a run visible in the detail
   * surface after it has left the table, because a run that is not in `rows`
   * has no way of being found here.
   */
  const selectedRun = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId]
  );

  /**
   * With a permanent detail column, "nothing selected" is an empty box beside a
   * full table — so the first listed run is picked automatically once a load
   * settles, and again whenever the previous pick has left the list.
   *
   * The guards are what keep this from looping: it runs only after loading ends
   * (during a refetch `rows` still holds the PREVIOUS page, and selecting from
   * that would pick a row about to disappear), and it returns early the moment
   * the selection is present in `rows` — which the `setSelectedId` below makes
   * true on the very next pass.
   *
   * It sets no `drawerOpen`. On a small screen the overlay stays shut until the
   * reader asks for it.
   */
  useEffect(() => {
    if (loading) return;
    if (rows.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (rows.some((r) => r.id === selectedId)) return;
    setSelectedId(rows[0].id);
  }, [rows, loading, selectedId]);

  const handleSelectRun = useCallback((run) => {
    setSelectedId(run.id);
    setDrawerOpen(true);
  }, []);

  /**
   * Any filter change starts at page one.
   *
   * An offset kept across a filter change points into the OLD population: 100
   * runs filtered down to 12 with `offset=75` renders an empty table that looks
   * like "no results" when the real answer is twelve rows on page one.
   *
   * This stays with the page rather than moving into the filter bar: the offset
   * it invalidates lives here, and a child that reset a parent's pagination
   * through a side effect would be the harder thing to reason about.
   */
  const applyScope = (next) => {
    setScopeFilter(next);
    setOffset(0);
  };
  const applyStatus = (next) => {
    setStatusFilter(next);
    setOffset(0);
  };
  const applyPeriod = (next) => {
    setPeriodDays(Number(next));
    setOffset(0);
  };
  const resetFilters = useCallback(() => {
    setScopeFilter("data");
    setStatusFilter("");
    setPeriodDays(DEFAULT_PERIOD_DAYS);
    setOffset(0);
  }, []);

  /** Same reason as above: a new page size re-slices the list from the start. */
  const applyLimit = useCallback((next) => {
    setLimit(next);
    setOffset(0);
  }, []);

  const rangeLabel = periodLabel(periodDays);
  const lastSync = status?.last_successful_sync || null;

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-foreground">Sync-Läufe</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Status und Verlauf der Bexio-Synchronisationen.
        </p>
      </header>

      <BexioSyncRunsKpiRow
        statusLoading={statusLoading}
        statusError={statusError}
        lastSync={lastSync}
        summaryLoading={summaryLoading}
        summaryError={summaryError}
        summary={summary}
        rangeLabel={rangeLabel}
      />

      <BexioSyncRunsMetaStrip
        status={status}
        statusLoading={statusLoading}
        statusError={statusError}
      />

      <BexioSyncRunsFilterBar
        scopeFilter={scopeFilter}
        statusFilter={statusFilter}
        periodDays={periodDays}
        total={total}
        onScopeChange={applyScope}
        onStatusChange={applyStatus}
        onPeriodChange={applyPeriod}
      />

      {/* The table and the detail column are siblings, not overlays: reading a
          run and scanning the list are the same task at this width. Below `lg`
          the column hides itself and the drawer takes over. */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <BexioSyncRunsTable
            rows={rows}
            total={total}
            offset={offset}
            limit={limit}
            loading={loading}
            error={error}
            filtered={Boolean(statusFilter) || scopeFilter !== "data"}
            selectedId={selectedId}
            onRetry={reload}
            onResetFilters={resetFilters}
            onSelectRun={handleSelectRun}
            onOffsetChange={setOffset}
            onLimitChange={applyLimit}
          />
        </div>

        <BexioSyncRunDetailPanel
          run={selectedRun}
          emptyHint={
            error
              ? "Sync-Läufe konnten nicht geladen werden."
              : "Keine Sync-Läufe im gewählten Zeitraum."
          }
        />
      </div>

      {/* Below the table rather than beside it: it answers a different question
          ("how has the fortnight gone") over a different window, and reading it
          is not part of scanning the list. It gets the full width for 14 bars. */}
      <BexioSyncRunsDailyChart
        daily={daily}
        loading={dailyLoading}
        error={dailyError}
      />

      {/* Only below `lg`, where the column above is hidden. `isCompact` and that
          column's `hidden lg:block` read the same 1024px boundary, so there is no
          width at which a reader gets neither. */}
      <BexioSyncRunDetailDrawer
        open={isCompact && drawerOpen && Boolean(selectedRun)}
        onOpenChange={(o) => {
          if (!o) setDrawerOpen(false);
        }}
        run={selectedRun}
      />
    </div>
  );
}
